import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import SortableTableHeader from '../../components/SortableTableHeader';
import { getIncomeByCustomerSummary, getIncomeByCustomerDetail } from '../../utils/reportApi';
import type { IncomeByCustomerSummaryReport, IncomeByCustomerDetailReport } from '../../types/report';
import { ReportLayout } from '../../components/reports/ReportLayout';
import IncomeByCustomerInvoiceDetailModal from '../../components/reports/IncomeByCustomerInvoiceDetailModal';

type DetailSortColumn = 'invoice_number' | 'txn_date' | 'revenue' | 'cogs' | 'income';

const formatAmount = (value: string) => formatCurrency(parseFloat(value));

const incomeClassName = (value: string) =>
  parseFloat(value) < 0 ? 'text-red-600' : '';

const IncomeByCustomerDetailView = ({
  customerId,
  startDate,
  endDate,
}: {
  customerId: number;
  customerName: string;
  startDate: string;
  endDate: string;
}) => {
  const [detail, setDetail] = useState<IncomeByCustomerDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lineDetailInvoiceId, setLineDetailInvoiceId] = useState<number | null>(null);

  const [sortColumn, setSortColumn] = useState<DetailSortColumn>('txn_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: DetailSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedInvoices = React.useMemo(() => {
    const invoices = detail?.customers?.[0]?.invoices || [];
    return [...invoices].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'invoice_number') comparison = a.invoice_number.localeCompare(b.invoice_number);
      else if (sortColumn === 'txn_date') comparison = String(a.txn_date).localeCompare(String(b.txn_date));
      else if (sortColumn === 'revenue') comparison = parseFloat(a.revenue) - parseFloat(b.revenue);
      else if (sortColumn === 'cogs') comparison = parseFloat(a.cogs) - parseFloat(b.cogs);
      else if (sortColumn === 'income') comparison = parseFloat(a.income) - parseFloat(b.income);

      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [detail, sortColumn, sortDir]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const data = await getIncomeByCustomerDetail({
          customer_id: customerId,
          start_date: startDate,
          end_date: endDate,
        });
        setDetail(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [customerId, startDate, endDate]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
        <p className="text-slate-500 font-medium">Loading details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (!detail || !detail.customers[0]) {
    return <div className="p-8 text-center text-slate-500">No data found.</div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTableHeader<DetailSortColumn> label="Invoice #" column="invoice_number" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
              <SortableTableHeader<DetailSortColumn> label="Txn Date" column="txn_date" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
              <SortableTableHeader<DetailSortColumn> label="Revenue" column="revenue" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
              <SortableTableHeader<DetailSortColumn> label="COGS" column="cogs" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
              <SortableTableHeader<DetailSortColumn> label="Income" column="income" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedInvoices.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                  No invoices found for this period.
                </td>
              </tr>
            ) : (
              sortedInvoices.map((inv) => (
                <tr
                  key={inv.invoice_id}
                  onClick={() => setLineDetailInvoiceId(inv.invoice_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLineDetailInvoiceId(inv.invoice_id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  title="View line-level gross profit"
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 font-medium text-blue-600">
                    {inv.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{inv.txn_date}</td>
                  <td className="px-6 py-4 text-right text-slate-600 font-medium">
                    {formatAmount(inv.revenue)}
                  </td>
                  <td className="px-6 py-4 text-right text-slate-600 font-medium">
                    {formatAmount(inv.cogs)}
                  </td>
                  <td className={`px-6 py-4 text-right font-medium ${incomeClassName(inv.income)}`}>
                    {formatAmount(inv.income)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td colSpan={2} className="px-6 py-4 font-semibold text-slate-900 text-right">
                Total
              </td>
              <td className="px-6 py-4 text-right font-semibold text-slate-900">
                {formatAmount(detail.totals.revenue)}
              </td>
              <td className="px-6 py-4 text-right font-semibold text-slate-900">
                {formatAmount(detail.totals.cogs)}
              </td>
              <td className={`px-6 py-4 text-right font-semibold text-slate-900 text-base ${incomeClassName(detail.totals.income)}`}>
                {formatAmount(detail.totals.income)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {lineDetailInvoiceId !== null && (
        <IncomeByCustomerInvoiceDetailModal
          invoiceId={lineDetailInvoiceId}
          onClose={() => setLineDetailInvoiceId(null)}
        />
      )}
    </>
  );
};

type SummarySortColumn = 'customer' | 'invoices' | 'revenue' | 'cogs' | 'income';

const IncomeByCustomerSummary = () => {
  const defaultEndDate = new Date().toISOString().split('T')[0];
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split('T')[0];

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false);

  const [report, setReport] = useState<IncomeByCustomerSummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortColumn, setSortColumn] = useState<SummarySortColumn>('income');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: SummarySortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedCustomers = React.useMemo(() => {
    if (!report) return [];
    return [...report.customers].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'customer') {
        comparison = a.display_name.localeCompare(b.display_name);
      } else if (sortColumn === 'invoices') {
        comparison = a.invoice_count - b.invoice_count;
      } else if (sortColumn === 'revenue') {
        comparison = parseFloat(a.revenue) - parseFloat(b.revenue);
      } else if (sortColumn === 'cogs') {
        comparison = parseFloat(a.cogs) - parseFloat(b.cogs);
      } else if (sortColumn === 'income') {
        comparison = parseFloat(a.income) - parseFloat(b.income);
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [report, sortColumn, sortDir]);

  const [view, setView] = useState<'summary' | 'detail'>('summary');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);

  useEffect(() => {
    if (view === 'summary') {
      const fetchReport = async () => {
        try {
          setLoading(true);
          const data = await getIncomeByCustomerSummary({
            start_date: startDate,
            end_date: endDate,
            include_zero_balances: includeZeroBalances,
          });
          setReport(data);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load report');
        } finally {
          setLoading(false);
        }
      };
      fetchReport();
    }
  }, [startDate, endDate, includeZeroBalances, view]);

  const handleDrillDown = (customerId: number, customerName: string) => {
    setSelectedCustomerId(customerId);
    setSelectedCustomerName(customerName);
    setView('detail');
  };

  const handleBackToSummary = () => {
    setView('summary');
    setSelectedCustomerId(null);
    setSelectedCustomerName(null);
  };

  const handleFilterChange = () => {
    if (view === 'detail') handleBackToSummary();
  };

  const filters = (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4 flex-wrap">
      <div className="flex-1 max-w-xs">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
          Start Date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              handleFilterChange();
            }}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
      </div>
      <div className="flex-1 max-w-xs">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
          End Date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              handleFilterChange();
            }}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 pb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={includeZeroBalances}
          onChange={(e) => {
            setIncludeZeroBalances(e.target.checked);
            handleFilterChange();
          }}
          className="rounded border-slate-300 text-slate-800 focus:ring-slate-800"
        />
        <span className="text-sm text-slate-600">Include zero balances</span>
      </label>
    </div>
  );

  return (
    <ReportLayout
      title={view === 'summary' ? 'Income by Customer Summary' : 'Income by Customer Detail'}
      description={
        view === 'summary'
          ? 'Gross profit (revenue minus COGS) per customer. Revenue is pre-tax; VAT excluded.'
          : `Invoices for ${selectedCustomerName}`
      }
      filters={filters}
      onBack={view === 'detail' ? handleBackToSummary : undefined}
      backLabel="Back to summary"
      onExport={() => alert('Export functionality to be implemented')}
      onPrint={() => window.print()}
    >
      {view === 'summary' ? (
        loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 font-medium">Loading report data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : !report ? (
          <div className="p-8 text-center text-slate-500">No data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortableTableHeader<SummarySortColumn> label="Customer" column="customer" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
                  <SortableTableHeader<SummarySortColumn> label="Invoices" column="invoices" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortableTableHeader<SummarySortColumn> label="Revenue" column="revenue" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableTableHeader<SummarySortColumn> label="COGS" column="cogs" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableTableHeader<SummarySortColumn> label="Income" column="income" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                      No income found for this period.
                    </td>
                  </tr>
                ) : (
                  sortedCustomers.map((customer) => (
                    <tr
                      key={customer.customer_id}
                      onClick={() => handleDrillDown(customer.customer_id, customer.display_name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDrillDown(customer.customer_id, customer.display_name);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      title="View customer details"
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {customer.display_name}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200">
                          {customer.invoice_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-slate-600 font-medium">
                        {formatAmount(customer.revenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-slate-600 font-medium">
                        {formatAmount(customer.cogs)}
                      </td>
                      <td
                        className={`px-6 py-4 text-right font-medium text-base ${
                          parseFloat(customer.income) < 0 ? 'text-red-600' : 'text-slate-900'
                        }`}
                      >
                        {formatAmount(customer.income)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    Total ({report.totals.customer_count || report.customers.length} Customers)
                  </td>
                  <td></td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900">
                    {formatAmount(report.totals.revenue)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900">
                    {formatAmount(report.totals.cogs)}
                  </td>
                  <td className={`px-6 py-4 text-right font-semibold text-slate-900 text-lg ${incomeClassName(report.totals.income)}`}>
                    {formatAmount(report.totals.income)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      ) : (
        selectedCustomerId && (
          <IncomeByCustomerDetailView
            customerId={selectedCustomerId}
            customerName={selectedCustomerName!}
            startDate={startDate}
            endDate={endDate}
          />
        )
      )}
    </ReportLayout>
  );
};

export default IncomeByCustomerSummary;
